import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Grid,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import moment from 'moment';
import { getInventoryListDateDetail } from '../../services/api';
import { THEME_COLORS } from '../../shared/constants/theme';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  getBookingDrawerHeaderSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPanelSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
} from '../../utils/bookingDrawerLayout';

const formatBookingPercentage = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(2)}%`;
};

const formatDateLabel = (dateStr) => {
  if (!dateStr) return '—';
  const m = moment(dateStr, 'YYYY-MM-DD', true);
  return m.isValid() ? m.format('ddd, D MMM YYYY') : dateStr;
};

const SummaryTile = ({ label, value, isDark, drawerTheme, highlight = false }) => (
  <Box
    sx={{
      ...getBookingDrawerPanelSx(isDark),
      p: 1.5,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: drawerTheme.subtleText,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        fontSize: '0.68rem',
        mb: 0.5,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="h6"
      sx={{
        fontWeight: 700,
        color: highlight ? BOOKING_DRAWER_ACCENT : drawerTheme.text,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const getDrawerChartSx = (isDark, drawerTheme) => ({
  '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
    stroke: isDark ? alpha('#ffffff', 0.2) : 'rgba(15, 23, 42, 0.18)',
  },
  '& .MuiChartsGrid-line': {
    stroke: isDark ? alpha('#ffffff', 0.08) : 'rgba(15, 23, 42, 0.08)',
    strokeDasharray: '4 5',
  },
  '& .MuiChartsAxis-tickLabel': {
    fill: drawerTheme.subtleText,
    fontWeight: 700,
    fontSize: 11,
  },
  '& .MuiChartsLegend-series text': {
    fill: drawerTheme.text,
    fontWeight: 700,
    fontSize: 12,
  },
});

const InventoryDateDetailDrawer = ({ open, onClose, date, dateIso, themeMode = 'light' }) => {
  const resolvedDate = date || dateIso;
  const isDark = themeMode === 'dark';
  const drawerTheme = getBookingDrawerTheme(isDark);
  const chartSx = useMemo(() => getDrawerChartSx(isDark, drawerTheme), [isDark, drawerTheme]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!open || !resolvedDate) {
      setDetail(null);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getInventoryListDateDetail(resolvedDate);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load inventory detail');
          setDetail(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, resolvedDate]);

  const summary = detail?.summary || {};
  const roomTypes = detail?.room_types || [];
  const bookingSplit = detail?.booking_split || [];

  const occupancyDataset = useMemo(
    () =>
      roomTypes.map((row) => ({
        name: row.room_type_name || 'Room type',
        booked: Number(row.booked_count) || 0,
        notBooked: Number(row.not_booked_count) || 0,
      })),
    [roomTypes],
  );

  const splitChartConfig = useMemo(() => {
    const splitColors = [
      BOOKING_DRAWER_ACCENT,
      isDark ? alpha(BOOKING_DRAWER_ACCENT, 0.55) : alpha(THEME_COLORS.PRIMARY, 0.65),
      isDark ? alpha('#ffffff', 0.18) : '#cbd5e1',
    ];
    const dataset = bookingSplit.map((row, rowIndex) => {
      const entry = { name: row.label || '—' };
      bookingSplit.forEach((_, colIndex) => {
        entry[`split${colIndex}`] = rowIndex === colIndex ? Number(row.value) || 0 : 0;
      });
      return entry;
    });
    const series = bookingSplit.map((row, index) => ({
      dataKey: `split${index}`,
      label: row.label || '—',
      color: splitColors[index % splitColors.length],
      stack: 'bookingSplit',
      borderRadius: 4,
    }));
    return { dataset, series };
  }, [bookingSplit, isDark]);

  const bookingPct = formatBookingPercentage(summary.booking_percentage);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          ...getBookingDrawerPaperSx(isDark),
          width: BOOKING_DRAWER_WIDTH_SX,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header — same pattern as Check-in drawer */}
        <Box
          sx={{
            ...getBookingDrawerHeaderSx(isDark),
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Inventory2OutlinedIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: isDark ? drawerTheme.text : '#333',
              }}
            >
              Inventory detail
            </Typography>
            {resolvedDate ? (
              <>
                <Typography variant="body2" sx={{ color: drawerTheme.subtleText, mx: 0.5 }}>
                  -
                </Typography>
                <Chip
                  label={formatDateLabel(resolvedDate)}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    backgroundColor: isDark ? alpha(BOOKING_DRAWER_ACCENT, 0.14) : alpha(BOOKING_DRAWER_ACCENT, 0.12),
                    color: BOOKING_DRAWER_ACCENT,
                    border: `1px solid ${alpha(BOOKING_DRAWER_ACCENT, isDark ? 0.35 : 0.4)}`,
                    '& .MuiChip-label': { px: 1.25 },
                  }}
                />
              </>
            ) : null}
          </Box>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: { xs: 1.5, sm: 2 },
            backgroundColor: isDark ? THEME_COLORS.BACKGROUND_DARK : '#f2f4f7',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress size={36} sx={{ color: BOOKING_DRAWER_ACCENT }} />
            </Box>
          ) : error ? (
            <Box
              sx={{
                ...getBookingDrawerPanelSx(isDark),
                p: 2,
                borderColor: alpha('#ef4444', 0.4),
              }}
            >
              <Typography variant="body2" sx={{ color: '#ef4444' }}>
                {error}
              </Typography>
            </Box>
          ) : detail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={4}>
                  <SummaryTile
                    label="Total inventory"
                    value={summary.total_inventory ?? '—'}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <SummaryTile
                    label="Booked"
                    value={summary.booked_count ?? '—'}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                    highlight
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <SummaryTile
                    label="Not booked"
                    value={summary.not_booked_count ?? '—'}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <SummaryTile
                    label="Offline booked"
                    value={summary.offline_booked ?? '—'}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <SummaryTile
                    label="Online booked"
                    value={summary.online_booked ?? '—'}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <SummaryTile
                    label="Booking %"
                    value={bookingPct}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                    highlight
                  />
                </Grid>
              </Grid>

              <Box sx={{ ...getBookingDrawerPanelSx(isDark), p: { xs: 1.5, sm: 2 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: drawerTheme.text, mb: 1.5 }}
                >
                  Room type occupancy
                </Typography>
                {roomTypes.length > 0 ? (
                  <BarChart
                    dataset={occupancyDataset}
                    height={280}
                    margin={{ top: 12, right: 12, left: 36, bottom: 48 }}
                    xAxis={[
                      {
                        scaleType: 'band',
                        dataKey: 'name',
                        categoryGapRatio: 0.32,
                      },
                    ]}
                    yAxis={[
                      {
                        width: 36,
                        tickLabelStyle: { fill: drawerTheme.subtleText, fontSize: 11, fontWeight: 700 },
                      },
                    ]}
                    series={[
                      {
                        dataKey: 'booked',
                        label: 'Booked',
                        color: BOOKING_DRAWER_ACCENT,
                        stack: 'occupancy',
                        borderRadius: 4,
                      },
                      {
                        dataKey: 'notBooked',
                        label: 'Not booked',
                        color: isDark ? alpha('#ffffff', 0.18) : '#cbd5e1',
                        stack: 'occupancy',
                        borderRadius: 4,
                      },
                    ]}
                    slotProps={{
                      legend: {
                        position: { vertical: 'top', horizontal: 'right' },
                        direction: 'row',
                        itemGap: 12,
                      },
                    }}
                    grid={{ horizontal: true, vertical: false }}
                    sx={chartSx}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: drawerTheme.subtleText }}>
                    No room type data for this date.
                  </Typography>
                )}
              </Box>

              <Box sx={{ ...getBookingDrawerPanelSx(isDark), p: { xs: 1.5, sm: 2 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: drawerTheme.text, mb: 1.5 }}
                >
                  Booking split
                </Typography>
                {bookingSplit.length > 0 ? (
                  <BarChart
                    dataset={splitChartConfig.dataset}
                    height={240}
                    margin={{ top: 12, right: 12, left: 36, bottom: 48 }}
                    xAxis={[
                      {
                        scaleType: 'band',
                        dataKey: 'name',
                        categoryGapRatio: 0.4,
                      },
                    ]}
                    yAxis={[
                      {
                        width: 36,
                        tickLabelStyle: { fill: drawerTheme.subtleText, fontSize: 11, fontWeight: 700 },
                      },
                    ]}
                    series={splitChartConfig.series}
                    slotProps={{ legend: { hidden: true } }}
                    grid={{ horizontal: true, vertical: false }}
                    sx={chartSx}
                  />
                ) : (
                  <Typography variant="body2" sx={{ color: drawerTheme.subtleText }}>
                    No booking split data for this date.
                  </Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: drawerTheme.subtleText, textAlign: 'center', py: 4 }}>
              Select a date to view inventory detail.
            </Typography>
          )}
        </Box>

        {/* Footer — same pattern as Check-in drawer */}
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
          }}
        >
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={getBookingDrawerOutlinedButtonSx(isDark)}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default InventoryDateDetailDrawer;
